import React,{useEffect} from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate,useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { HelmetProvider } from 'react-helmet-async';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import './instrument';
import ForgotPassword from './pages/auth/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import SurveyEditor from './pages/survey/SurveyEditor';
import TemplatesPage from './pages/survey/TemplatesPage';
import SubscriptionSuccess from './pages/subscription/SubscriptionSuccess';
import theme from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import PrivateRoute from './components/PrivateRoute';
import NotFound from './pages/NotFound';
import { store } from './store';
import { LanguageProvider } from './contexts/LanguageContext';
import CreateWithAI from './pages/survey/CreateWithAI';
import Onboarding from './pages/auth/Onboarding';
import ReferralHandler from './components/ReferralHandler';
import SurveyForm from './pages/form/SurveyForm';
import SurveyResponses from './pages/survey/SurveyResponses';
import { SnackbarProvider } from 'notistack';
import { SurveyProvider } from './contexts/SurveyContext';
import Referrals from './pages/admin/Referrals';
import AdminImpersonationBanner from './components/AdminImpersonationBanner';
import UnsubscribePage from './pages/UnsubscribePage';
import Documentation from './pages/Documentation';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import UsersManagement from './pages/admin/UsersManagement';
import SurveysManagement from './pages/admin/SurveysManagement';
import ResponsesManagement from './pages/admin/ResponsesManagement';
import EmailManagement from './pages/admin/EmailManagement';
import AdminAuthGuard from './components/AdminAuthGuard';
import CookieConsentBanner from './components/CookieConsentBanner';
import PrivacyPolicy from './pages/PrivacyPolicy';

function App() {
   function ClarityLoader() {
    const location = useLocation();

    useEffect(() => {
      // adjust match to include basename '/app' if you prefer:
      if (!location || !location.pathname) return;
     
        if (window.__clarity_loaded__) return;
        window.__clarity_loaded__ = true;

        // Replace YOUR_CLARITY_ID with your real Clarity project id
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r); t.async=1; t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "twk2f7jx58");
      // }
      console.log('clarity initialized');
      
    }, []);

    return null;
  }
  return (
    <HelmetProvider>
      <AuthProvider>
        <AnalyticsProvider>
          <LanguageProvider>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <Provider store={store}>
                <SnackbarProvider maxSnack={3}>
                  {/* <SurveyProvider> */}
                  <Router basename="/app">
                     <ClarityLoader />
                    <AdminImpersonationBanner />
                    <ReferralHandler />
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<SignUp />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/onboarding" element={
                        <PrivateRoute>
                          <Onboarding />
                        </PrivateRoute>
                      } />
                      <Route path="/dashboard" element={
                        <PrivateRoute>
                          <Dashboard />
                        </PrivateRoute>
                      } />
                      <Route path="/profile" element={
                        <PrivateRoute>
                          <Profile />
                        </PrivateRoute>
                      } />
                      <Route path="/survey/edit/:surveyId" element={
                        <PrivateRoute>
                          <SurveyEditor />
                        </PrivateRoute>
                      } />
                      <Route path="/surveys/templates" element={
                        <PrivateRoute>
                          <TemplatesPage />
                        </PrivateRoute>
                      } />
                      <Route path="/subscription" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/survey/create-with-ai" element={
                        <PrivateRoute>
                          <CreateWithAI />
                        </PrivateRoute>
                      } />
                      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                      <Route path="/form/:surveyId" element={<SurveyForm />} />
                      <Route path="/survey/responses/:surveyId" element={<SurveyResponses />} />
                      <Route path="/admin/referrals" element={
                        <AdminAuthGuard>
                          <AdminLayout>
                            <Referrals />
                          </AdminLayout>
                        </AdminAuthGuard>
                      } />

                      {/* Admin Panel Routes */}
                      <Route path="/admin" element={
                        <AdminAuthGuard>
                          <AdminLayout>
                            <AdminDashboard />
                          </AdminLayout>
                        </AdminAuthGuard>
                      } />
                      <Route path="/admin/users" element={
                        <AdminAuthGuard>
                          <AdminLayout>
                            <UsersManagement />
                          </AdminLayout>
                        </AdminAuthGuard>
                      } />
                      <Route path="/admin/surveys" element={
                        <AdminAuthGuard>
                          <AdminLayout>
                            <SurveysManagement />
                          </AdminLayout>
                        </AdminAuthGuard>
                      } />
                      <Route path="/admin/responses" element={
                        <AdminAuthGuard>
                          <AdminLayout>
                            <ResponsesManagement />
                          </AdminLayout>
                        </AdminAuthGuard>
                      } />
                      <Route path="/admin/emails" element={
                        <AdminAuthGuard>
                          <AdminLayout>
                            <EmailManagement />
                          </AdminLayout>
                        </AdminAuthGuard>
                      } />

                      <Route path="/unsubscribe" element={<UnsubscribePage />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/documentations" element={<Documentation />} />
                      <Route path="/documentations/:section" element={<Documentation />} />
                      <Route path="/documentations/:section/:page" element={<Documentation />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    <CookieConsentBanner />
                  </Router>
                  {/* </SurveyProvider> */}
                </SnackbarProvider>
              </Provider>
            </ThemeProvider>
          </LanguageProvider>
        </AnalyticsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;